/**
 * Client Profile Page - "Digital Boiler Passport"
 * Визуализация состояния котла, история обслуживания, хранилище документов
 * Blue-700 header, Slate-50 background, Tab navigation
 */

import { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Home,
  History,
  FileText,
  MapPin,
  Clock,
  Star,
  User,
  Phone,
  Plus,
  Image as ImageIcon,
  FileCheck,
  AlertCircle,
  CheckCircle2,
  Download,
  Trash2,
  X
} from 'lucide-react';
import {
  fetchUserProfile,
  fetchServiceHistory,
  fetchDocuments,
  uploadDocument,
  downloadDocument,
  deleteDocument,
  readFileAsBase64,
  type ClientProfile,
  type ServiceHistoryItem,
  type ClientDocument,
  type ClientDocType
} from '@/lib/userApi';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

type TabType = 'status' | 'history' | 'documents';

// Status calculation based on lastService date
interface BoilerStatus {
  color: 'blue' | 'green' | 'yellow' | 'red';
  label: string;
  description: string;
  progress: number; // 0-100
  daysRemaining: number | null;
}

function calculateBoilerStatus(lastService: string | null | undefined): BoilerStatus {
  if (!lastService) {
    return {
      color: 'blue',
      label: 'Новый',
      description: 'Система активна. Ожидание первого ТО',
      progress: 100,
      daysRemaining: null,
    };
  }

  const lastServiceDate = new Date(lastService);
  const now = new Date();
  const daysSinceService = Math.floor(
    (now.getTime() - lastServiceDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const daysRemaining = 365 - daysSinceService;
  const progress = Math.max(0, Math.min(100, (daysRemaining / 365) * 100));

  if (daysSinceService > 365) {
    return {
      color: 'red',
      label: 'Просрочено',
      description: 'ТО просрочено. Требуется обслуживание',
      progress: 0,
      daysRemaining: daysRemaining,
    };
  }

  if (daysSinceService > 335) {
    return {
      color: 'yellow',
      label: 'Внимание',
      description: 'Рекомендуется провести ТО',
      progress,
      daysRemaining,
    };
  }

  return {
    color: 'green',
    label: 'В норме',
    description: 'Системы в норме',
    progress,
    daysRemaining,
  };
}

const STATUS_COLORS = {
  blue: { stroke: '#2563eb', bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-200' },
  green: { stroke: '#16a34a', bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' },
  yellow: { stroke: '#ca8a04', bg: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-200' },
  red: { stroke: '#dc2626', bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' },
};

// Boiler SVG Icon
function BoilerIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Main body */}
      <rect x="12" y="8" width="40" height="48" rx="4" fill="#f1f5f9" stroke="#64748b" strokeWidth="2" />
      {/* Control panel */}
      <rect x="16" y="12" width="32" height="12" rx="2" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1" />
      {/* Display */}
      <rect x="20" y="15" width="14" height="6" rx="1" fill="#1e293b" />
      {/* LED indicators */}
      <circle cx="40" cy="18" r="2" fill="#22c55e" />
      <circle cx="40" cy="18" r="2" fill="#22c55e">
        <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* Flame window */}
      <rect x="18" y="32" width="28" height="18" rx="2" fill="#0f172a" />
      {/* Flame */}
      <path
        d="M32 46c-4 0-7-3-7-7 0-3 2-5 4-7 1 2 2 3 3 3s2-1 3-3c2 2 4 4 4 7 0 4-3 7-7 7z"
        fill="#f97316"
      >
        <animate attributeName="d"
          values="M32 46c-4 0-7-3-7-7 0-3 2-5 4-7 1 2 2 3 3 3s2-1 3-3c2 2 4 4 4 7 0 4-3 7-7 7z;
                  M32 47c-4 0-6-4-6-7 0-3 1-5 3-7 1 2 2 4 3 4s2-2 3-4c2 2 3 4 3 7 0 3-2 7-6 7z;
                  M32 46c-4 0-7-3-7-7 0-3 2-5 4-7 1 2 2 3 3 3s2-1 3-3c2 2 4 4 4 7 0 4-3 7-7 7z"
          dur="1s" repeatCount="indefinite" />
      </path>
      <path
        d="M32 44c-2 0-4-2-4-4 0-2 1-3 2-4 0.5 1 1 2 2 2s1.5-1 2-2c1 1 2 2 2 4 0 2-2 4-4 4z"
        fill="#fbbf24"
      />
      {/* Pipes */}
      <rect x="8" y="20" width="4" height="6" rx="1" fill="#94a3b8" />
      <rect x="52" y="20" width="4" height="6" rx="1" fill="#94a3b8" />
      <rect x="8" y="40" width="4" height="6" rx="1" fill="#94a3b8" />
      <rect x="52" y="40" width="4" height="6" rx="1" fill="#94a3b8" />
    </svg>
  );
}

// Circular progress component
function CircularProgress({
  progress,
  color,
  size = 200,
}: {
  progress: number;
  color: keyof typeof STATUS_COLORS;
  size?: number;
}) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={STATUS_COLORS[color].stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <BoilerIcon className="h-24 w-24" />
      </div>
    </div>
  );
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatPrice(price: string | null): string {
  if (!price) return '-';
  return new Intl.NumberFormat('ru-RU').format(Number(price)) + ' ₽';
}

function getOrderTypeLabel(type: string): string {
  const typeMap: Record<string, string> = {
    VIDEO_CONSULT: 'Видеоконсультация',
    REPAIR: 'Ремонт',
    MAINTENANCE: 'ТО',
  };
  return typeMap[type] || type;
}

function getOrderTypeIcon(type: string) {
  const iconMap: Record<string, string> = {
    VIDEO_CONSULT: '📹',
    REPAIR: '🔧',
    MAINTENANCE: '🛠️',
  };
  return iconMap[type] || '📋';
}

const DOC_TYPE_LABELS: Record<ClientDocType, string> = {
  CLIENT_EQUIPMENT_PASSPORT: 'Паспорт оборудования',
  CLIENT_VDGO_CONTRACT: 'Договор ВДГО',
  CLIENT_SERVICE_ACT: 'Акт выполненных работ',
};

// Status Tab
function StatusTab({ profile }: { profile: ClientProfile }) {
  const equipment = profile.equipment[0];
  const status = useMemo(
    () => calculateBoilerStatus(equipment?.lastService),
    [equipment?.lastService]
  );
  const colors = STATUS_COLORS[status.color];

  return (
    <div className="flex flex-col items-center space-y-6 p-4">
      {/* Status Widget */}
      <div className={`w-full rounded-2xl ${colors.bg} p-6 ring-1 ${colors.ring}`}>
        <div className="flex flex-col items-center">
          <CircularProgress progress={status.progress} color={status.color} />

          <div className="mt-4 text-center">
            <Badge className={`${colors.bg} ${colors.text} mb-2 text-sm font-semibold`}>
              {status.label}
            </Badge>
            <p className="text-slate-700">{status.description}</p>
            {status.daysRemaining !== null && (
              <p className="mt-1 text-sm text-slate-500">
                {status.daysRemaining > 0
                  ? `До следующего ТО: ${status.daysRemaining} дн.`
                  : `Просрочено на ${Math.abs(status.daysRemaining)} дн.`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Equipment Info */}
      {equipment && (
        <Card className="w-full border-0 shadow-sm">
          <CardContent className="p-4">
            <h3 className="mb-3 font-semibold text-slate-900">Информация об оборудовании</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50">
                  <span className="text-xl">🔥</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">
                    {equipment.brand} {equipment.model}
                  </p>
                  <p className="text-sm text-slate-500">Газовый котёл</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Адрес</p>
                  <p className="text-sm text-slate-500">{equipment.address}</p>
                </div>
              </div>

              {equipment.lastService && (
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Последнее ТО</p>
                    <p className="text-sm text-slate-500">{formatDate(equipment.lastService)}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No equipment message */}
      {!equipment && (
        <Card className="w-full border-0 shadow-sm">
          <CardContent className="flex flex-col items-center p-6 text-center">
            <AlertCircle className="mb-2 h-12 w-12 text-slate-300" />
            <p className="text-slate-500">Оборудование не добавлено</p>
            <p className="text-sm text-slate-400">
              Обратитесь к боту для регистрации оборудования
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// History Tab
function HistoryTab({
  serviceHistory,
  isLoading,
}: {
  serviceHistory: ServiceHistoryItem[];
  isLoading: boolean;
}) {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-pulse text-slate-500">Загрузка истории...</div>
      </div>
    );
  }

  if (serviceHistory.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center p-4">
        <History className="mb-3 h-16 w-16 text-slate-300" />
        <div className="text-center text-slate-500">
          <div className="font-medium">Нет завершённых работ</div>
          <div className="text-sm">История обслуживания появится здесь</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 p-4">
        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-0 h-full w-0.5 bg-slate-200" />

          {serviceHistory.map((item) => (
            <div key={item.id} className="relative mb-6 pl-12">
              {/* Timeline dot */}
              <div className="absolute left-3 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white ring-2 ring-blue-500">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              </div>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getOrderTypeIcon(item.type)}</span>
                        <span className="font-semibold text-slate-900">
                          {getOrderTypeLabel(item.type)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        #{item.orderNumber} • {formatDate(item.completedAt)}
                      </p>
                    </div>
                    {item.finalPrice && (
                      <span className="font-bold text-slate-900">
                        {formatPrice(item.finalPrice)}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {item.description && (
                    <p className="mb-3 text-sm text-slate-600">{item.description}</p>
                  )}

                  {/* Master info */}
                  {item.master && (
                    <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                      <User className="h-4 w-4" />
                      <span>{item.master.name}</span>
                      {item.master.rating && (
                        <span className="flex items-center gap-0.5">
                          <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                          {item.master.rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Photos */}
                  {item.photos && item.photos.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {item.photos.map((photoUrl, photoIndex) => (
                        <button
                          key={photoIndex}
                          onClick={() => {
                            window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                            setSelectedPhoto(photoUrl);
                          }}
                          className="flex-shrink-0 overflow-hidden rounded-lg"
                        >
                          <img
                            src={photoUrl}
                            alt={`Фото ${photoIndex + 1}`}
                            className="h-16 w-16 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            className="absolute right-4 top-4 rounded-full bg-white/20 p-2"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <img
            src={selectedPhoto}
            alt="Фото акта"
            className="max-h-[80vh] max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </>
  );
}

// Documents Tab
function DocumentsTab({
  documents,
  isLoading,
  onUpload,
  onDownload,
  onDelete,
  isUploading,
  isBusy,
}: {
  documents: ClientDocument[];
  isLoading: boolean;
  onUpload: (type: ClientDocType, file: File) => void;
  onDownload: (doc: ClientDocument) => void;
  onDelete: (doc: ClientDocument) => void;
  isUploading: boolean;
  isBusy: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState<ClientDocType | null>(null);

  const documentsByType = useMemo(() => {
    const grouped: Record<ClientDocType, ClientDocument[]> = {
      CLIENT_EQUIPMENT_PASSPORT: [],
      CLIENT_VDGO_CONTRACT: [],
      CLIENT_SERVICE_ACT: [],
    };
    documents.forEach((doc) => {
      if (grouped[doc.type]) {
        grouped[doc.type].push(doc);
      }
    });
    return grouped;
  }, [documents]);

  const handleFileSelect = (type: ClientDocType) => {
    setSelectedType(type);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedType) {
      onUpload(selectedType, file);
      e.target.value = '';
    }
    setSelectedType(null);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-pulse text-slate-500">Загрузка документов...</div>
      </div>
    );
  }

  const categories: { type: ClientDocType; icon: React.ReactNode }[] = [
    { type: 'CLIENT_EQUIPMENT_PASSPORT', icon: <FileText className="h-5 w-5 text-blue-600" /> },
    { type: 'CLIENT_VDGO_CONTRACT', icon: <FileCheck className="h-5 w-5 text-green-600" /> },
    { type: 'CLIENT_SERVICE_ACT', icon: <ImageIcon className="h-5 w-5 text-orange-600" /> },
  ];

  return (
    <div className="space-y-4 p-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      {categories.map(({ type, icon }) => (
        <Card key={type} className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
                  {icon}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{DOC_TYPE_LABELS[type]}</h3>
                  <p className="text-sm text-slate-500">
                    {documentsByType[type].length} документ(ов)
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                  handleFileSelect(type);
                }}
                disabled={isUploading}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 transition-colors hover:bg-blue-200 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {documentsByType[type].length > 0 && (
              <div className="space-y-2">
                {documentsByType[type].map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between rounded-lg bg-slate-50 p-3"
                  >
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {doc.originalName}
                      </p>
                      <p className="text-xs text-slate-500">{formatDate(doc.createdAt)}</p>
                    </div>
                    <div className="ml-2 flex items-center gap-2">
                      {doc.verified ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <Clock className="h-4 w-4 text-slate-400" />
                      )}
                      <button
                        onClick={() => {
                          window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                          onDownload(doc);
                        }}
                        disabled={isBusy}
                        className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50"
                        title="Скачать"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
                          onDelete(doc);
                        }}
                        disabled={isBusy}
                        className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50"
                        title="Удалить"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {documentsByType[type].length === 0 && (
              <p className="text-center text-sm text-slate-400">Документов пока нет</p>
            )}
          </CardContent>
        </Card>
      ))}

      {(isUploading || isBusy) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="rounded-2xl bg-white p-6 text-center">
            <div className="mb-3 h-8 w-8 mx-auto animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
            <p className="text-slate-700">
              {isUploading ? 'Загрузка документа...' : 'Обработка...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ClientProfilePage() {
  const [activeTab, setActiveTab] = useState<TabType>('status');
  const queryClient = useQueryClient();

  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: ['userProfile'],
    queryFn: fetchUserProfile,
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['serviceHistory'],
    queryFn: fetchServiceHistory,
    enabled: !!profile && profile.role === 'CLIENT',
  });

  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: fetchDocuments,
    enabled: !!profile && profile.role === 'CLIENT',
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ type, file }: { type: ClientDocType; file: File }) => {
      const fileData = await readFileAsBase64(file);
      return uploadDocument({
        type,
        fileData,
        fileName: file.name,
        mimeType: file.type,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    },
    onError: () => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      window.Telegram?.WebApp?.showAlert?.('Ошибка загрузки документа');
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async (doc: ClientDocument) => {
      const blob = await downloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    },
    onError: () => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      window.Telegram?.WebApp?.showAlert?.('Ошибка скачивания документа');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (doc: ClientDocument) => {
      return deleteDocument(doc.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    },
    onError: () => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      window.Telegram?.WebApp?.showAlert?.('Ошибка удаления документа');
    },
  });

  if (profileLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Загрузка...</div>
      </div>
    );
  }

  if (profileError || !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-red-500">Ошибка загрузки профиля</div>
      </div>
    );
  }

  if (profile.role !== 'CLIENT') {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-slate-500">Доступ только для клиентов</div>
      </div>
    );
  }

  const clientProfile = profile as ClientProfile;
  const serviceHistory = historyData?.data || [];
  const documents = documentsData?.data || [];

  const tabs: { id: TabType; icon: React.ReactNode; label: string }[] = [
    { id: 'status', icon: <Home className="h-5 w-5" />, label: 'Главная' },
    { id: 'history', icon: <History className="h-5 w-5" />, label: 'История' },
    { id: 'documents', icon: <FileText className="h-5 w-5" />, label: 'Документы' },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-blue-700 px-4 py-4 text-white shadow-lg">
        <div className="flex items-center gap-3">
          {clientProfile.photoUrl ? (
            <img
              src={clientProfile.photoUrl}
              alt={clientProfile.name}
              className="h-12 w-12 rounded-full border-2 border-white/30 object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-xl font-bold">
              {clientProfile.name.charAt(0)}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{clientProfile.name}</h1>
            {clientProfile.phone && (
              <div className="flex items-center gap-1 text-sm text-blue-200">
                <Phone className="h-3 w-3" />
                {clientProfile.phone}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 pb-20">
        {activeTab === 'status' && <StatusTab profile={clientProfile} />}
        {activeTab === 'history' && (
          <HistoryTab serviceHistory={serviceHistory} isLoading={historyLoading} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab
            documents={documents}
            isLoading={documentsLoading}
            onUpload={(type, file) => uploadMutation.mutate({ type, file })}
            onDownload={(doc) => downloadMutation.mutate(doc)}
            onDelete={(doc) => {
              window.Telegram?.WebApp?.showConfirm?.(
                `Удалить "${doc.originalName}"?`,
                (confirmed) => {
                  if (confirmed) deleteMutation.mutate(doc);
                }
              );
            }}
            isUploading={uploadMutation.isPending}
            isBusy={downloadMutation.isPending || deleteMutation.isPending}
          />
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white px-6 py-2 shadow-lg">
        <div className="flex items-center justify-around">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                window.Telegram?.WebApp?.HapticFeedback?.selectionChanged();
                setActiveTab(tab.id);
              }}
              className={`relative flex flex-col items-center gap-1 rounded-xl px-4 py-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-700'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab.icon}
              <span className="text-xs font-medium">{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute -bottom-0 h-0.5 w-8 rounded-full bg-blue-700" />
              )}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
